import { Component, Injector, OnInit } from '@angular/core';
import { AbstractControl, Validators } from '@angular/forms';
import { PrFormConstructor } from '@app/components/pr-form/pr-form.contructor';
import { AttributionDao } from '@app/dao/attribution/attribution.dao';
import { ReferenceDao } from '@app/dao/reference/reference.dao';
import { UserDao } from '@app/dao/user/user.dao';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { combineLatest, Subject, map, shareReplay } from 'rxjs';
import { CommandEnum } from '../../definition/interface/enum';
import { SubscriptionFacadeService } from '../../services/subscription-facade.service';
import { EaSubscriptionBuilder } from './ea-subscription-form.builder';

@UntilDestroy()
@Component({
    selector: 'marital-status-form',
    templateUrl: './marital-status-form.component.html',
    styleUrls: ['./marital-status-form.component.scss'],
    standalone: false
})
export class MaritalStatusFormComponent
  extends EaSubscriptionBuilder
  implements OnInit
{
  subscriptions: any[] = [];
  loanCategories: any[] = [];
  loanTypes: any[] = [];
  doneSubject: Subject<void> = new Subject<void>();
  page: number = 1;
  initialMaritalStatus: any;
  initialMarriageRegime: any;
  maritalStatus: any[];
  marriageRegime: any[];
  filteredMarriageRegime: any[];
  sessionConsultant: boolean;
  productType: string;
  isCristalisOrganization: boolean;
  cristalisOrganizationId: number = 251251251;
  countries = [];
  reference$;
  options = [
    {
      value: 'SINGLE',
      label: 'Célibataire',
      suffix: { title: 'A' },
    },
    { value: 'MARRIED', label: 'Marié', suffix: { title: 'B' } },
    {
      value: 'CIVILPARTNER',
      label: 'Pacsé',
      suffix: { title: 'C' },
    },
    { value: 'DIVORCED', label: 'Divorcé', suffix: { title: 'D' } },
    {
      value: 'WIDOWER',
      label: 'Veuf/Veuve',
      suffix: { title: 'E' },
    },
    {
      value: 'LIVETOGETHER',
      label: 'En concubinage',
      suffix: { title: 'F' },
    },
  ];
  constructor(
    protected formConstructor: PrFormConstructor,
    private userDao: UserDao,
    private referenceDao: ReferenceDao,
    private attributionDao: AttributionDao,

    private subscriptionFacade: SubscriptionFacadeService,
    protected injector: Injector,
  ) {
    super(formConstructor, injector);
    // new add
  }

  ngOnInit() {
    this.reference$ = combineLatest([
      this.userDao.getCouple(),
      this.referenceDao.getMaritalStatus(),
      this.referenceDao.getMarriageRegime(),
      this.referenceDao.getCountries(),
    ]).pipe(
      map(([couple, maritalStatus, marriageRegime, countries]) => {
        this.maritalStatus = maritalStatus;
        this.marriageRegime = marriageRegime;
        this.countries = countries;
        this.filteredMarriageRegime = this.marriageRegime.filter(function (m) {
          return m.id === 'INDIVISION' || m.id === 'SEP_BIEN';
        });
        return {
          couple,
          maritalStatus,
          marriageRegime,
          countries,
        };
      }),
      shareReplay(1),
    );

    this.attributionDao.getOrganization().subscribe((data: any) => {
      this.isCristalisOrganization = this.cristalisOrganizationId === data.id;
    });
    this.sessionConsultant =
      !!this.subscriptionFacade?.getPledge() &&
      !!this.subscriptionFacade?.getUser()?.sessionConsultant;
    this.productType = this.subscriptionFacade?.getPledge()?.productType;
    const owner = this.subscriptionFacade.getPledge()?.owners?.[0];
    this.initialMaritalStatus = owner?.userMaritalSituation;
    this.initialMarriageRegime = owner?.marriageRegime;

    this.subscriptionFacade.initForm(this, {
      isCristalisOrganization: this.isCristalisOrganization,
      sessionConsultant: this.sessionConsultant,
      productType: this.productType,
    });
    this.owners
      ?.get(`${0}`)
      ?.get('userMaritalSituation')
      .valueChanges.pipe(untilDestroyed(this))
      .subscribe(value => {
        this.updateMaritalStatus(
          this.owners.get(`${0}`).get('userMaritalSituation'),
          this.owners.get(`${0}`).get('marriageRegime'),
          this.owners.get(`${0}`).get('unionDate'),
          this.owners.get(`${0}`).get('unionPlace'),
          this.owners.get(`${0}`).get('unionContract'),
          this.owners.get(`${0}`).get('userSpouseFirstName'),
          this.owners.get(`${0}`).get('userSpouseLastName'),
          this.owners.get(`${0}`).get('userSpouseBirthName'),
        );
      });
  }

  onSubmit() {
    const newMaritalStatus = this.owners
      ?.get(`${0}`)
      ?.get('userMaritalSituation').value;
    const newMarriageRegime = this.owners
      ?.get(`${0}`)
      ?.get('marriageRegime').value;

    if (
      newMaritalStatus !== this.initialMaritalStatus ||
      newMarriageRegime !== this.initialMarriageRegime
    ) {
      this.subscriptionFacade.setCommand([
        CommandEnum.GENERATE_ADDITIONAL_QUESTIONS,
      ]);
    }

    this.subscriptionFacade.submitPledge(this);
  }

  prev() {
    this.subscriptionFacade.previousStep();
  }

  selectedMarriageRegime(reference): any[] {
    const maritalSituation = this.owners
      ?.get(`${0}`)
      ?.get('userMaritalSituation').value;

    if (maritalSituation === 'MARRIED') {
      return reference?.marriageRegime || [];
    } else if (maritalSituation === 'CIVILPARTNER') {
      return this.filteredMarriageRegime;
    }

    return [];
  }
  updateMaritalStatus(
    userMaritalSituation: AbstractControl,
    marriageRegime: AbstractControl,
    unionDate: AbstractControl,
    unionPlace: AbstractControl,
    unionContract: AbstractControl,
    userSpouseFirstName: AbstractControl,
    userSpouseLastName: AbstractControl,
    userSpouseBirthName: AbstractControl,
  ) {
    if (this.isUnionRequired(this.productType, userMaritalSituation.value)) {
      marriageRegime.setValidators([Validators.required]);
      unionDate.setValidators([Validators.required]);
      unionPlace.setValidators([Validators.required]);
      unionContract.setValidators([Validators.required]);
      userSpouseFirstName.setValidators([Validators.required]);
      userSpouseLastName.setValidators([Validators.required]);
      userSpouseBirthName.setValidators([Validators.required]);
      this.marriageRegime &&
        this.marriageRegime[0].id &&
        marriageRegime.setValue(this.marriageRegime[0].id);
      marriageRegime.updateValueAndValidity();
      unionDate.updateValueAndValidity();
      unionPlace.updateValueAndValidity();
      unionContract.updateValueAndValidity();
      userSpouseFirstName.updateValueAndValidity();
      userSpouseLastName.updateValueAndValidity();
      userSpouseBirthName.updateValueAndValidity();
    } else {
      marriageRegime.setValidators(null);
      unionDate.setValidators(null);
      unionPlace.setValidators(null);
      unionContract.setValidators(null);
      userSpouseFirstName.setValidators(null);
      userSpouseLastName.setValidators(null);
      userSpouseBirthName.setValidators(null);
      marriageRegime.updateValueAndValidity();
      unionDate.updateValueAndValidity();
      unionPlace.updateValueAndValidity();
      unionContract.updateValueAndValidity();
      userSpouseFirstName.updateValueAndValidity();
      userSpouseLastName.updateValueAndValidity();
      userSpouseBirthName.updateValueAndValidity();
    }
  }
}
